import React, { useState } from "react";
import { Button, Form, Input } from "antd";
import { propertyService, userService } from "@iso/services";
import { SETUP_VARIANT } from "@iso/constants/onboarding";

// The setup flow's last step. Each Invite sends one invitation and clears the
// field so another can be sent; Skip / Finish ends onboarding.
const InviteStep = ({ variant, propertyId, invitedEmails, onInvited, onFinish, children }) => {
  const [form] = Form.useForm();
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const fail = (message) => {
    setErrMsg(typeof message === "string" && message ? message : "We couldn't send that invite. Please try again.");
    setLoading(false);
  };

  const handleFinish = ({ email }) => {
    const address = email.trim();
    setErrMsg("");
    setLoading(true);
    propertyService
      .inviteUser(propertyId, {
        user_email: address,
        user_name: address.split("@")[0],
        permission: "view",
      })
      .then((res) => {
        if (res && res.code === "0000") {
          setLoading(false);
          form.resetFields();
          onInvited(address);
        } else {
          fail(res && res.message);
        }
      })
      .catch(() => fail());
  };

  const isBusiness = variant === SETUP_VARIANT.BUSINESS;

  return (
    <>
      <h1 className="ob-title" tabIndex={-1}>
        {isBusiness
          ? "Invite your team"
          : "Invite your family, consultants and employees"}
      </h1>
      <p className="ob-lead">
        {isBusiness
          ? "Bring your staff and subcontractors into AirAgri so everyone works from the same safety information."
          : "Having everyone who works on your property in AirAgri makes for a safer, more efficient operation."}{" "}
        AirAgri doesn't charge per user, and the free account includes
        unlimited users and connections.
      </p>
      {errMsg && (
        <div className="ob-error" role="alert">
          {errMsg}
        </div>
      )}
      <Form name="onboarding_invite" form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="email"
          label="Email address"
          rules={[
            { required: true, whitespace: true, message: "Please enter an email address" },
            { type: "email", message: "Please enter a valid email address" },
            () => ({
              validator(_, value) {
                if (value && value.trim()) {
                  return userService.checkEmailExistsInProperty(
                    value.trim(),
                    propertyId
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder="name@example.com" autoComplete="off" />
        </Form.Item>
        {invitedEmails.length > 0 && (
          <p className="ob-note">
            Invite sent to {invitedEmails.join(", ")}. You can invite someone
            else, or finish.
          </p>
        )}
        <div className="ob-actions">
          <Button className="ob-btn-primary" htmlType="submit" loading={loading}>
            Invite
          </Button>
          <Button className="ob-btn-secondary" onClick={onFinish}>
            {invitedEmails.length > 0 ? "Finish setup" : "Skip for now"}
          </Button>
        </div>
      </Form>
      {children}
    </>
  );
};

export default InviteStep;
